import cx from 'classnames';
import { useEffect } from 'react';
import { Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { THEME } from '../../../config/commons';
import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { getCurrentTheme } from '../../../helpers/theme';
import { formatLongDateWithOffset } from '../../../utils/date';
import { LrvText } from '../../../common/components/LrvText/LrvText';
import ActionButton from '../../../common/components/buttons/ActionButton';
import { lrvConfirm } from '../../../common/components/LrvConfirm/LrvConfirm';
import { LrvDivider } from '../../../common/components/LrvDivider/LrvDivider';

import * as mfaSlice from './mfaSlice';
import styles from './TabMFA.module.scss';
import { MultifactorAuthenticationMethod } from './helpers';
import { AuthenticatorAppForm } from './AuthenticatorAppForm';
import { AuthenticatorEmailForm } from './AuthenticatorEmailForm';

export const TabMFA = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const theme = getCurrentTheme();
  const isLightTheme = theme === THEME.LIGHT;

  const {
    isRemoving,
    mfaAuthenticator,
    mfaEmail,
  } = useSelector((state: Store) => state.mfa);

  useEffect(() => {
    dispatch(mfaSlice.fetchMfas());
  }, [dispatch]);

  const showConfirmRemoveMfa = (method: MultifactorAuthenticationMethod) => {
    lrvConfirm({
      theme: 'light',
      title: t('mfa.removeConfirm.title'),
      icon: <ExclamationCircleOutlined className={styles.exclamationIcon} />,
      content: t('mfa.removeConfirm.description'),
      okText: t('actions.delete').toUpperCase(),
      cancelText: t('actions.cancel').toUpperCase(),
      okButtonProps: { id: 'confirm-remove-mfa', loading: isRemoving },
      cancelButtonProps: { id: 'cancel-remove-mfa' },
      onOk () {
        dispatch(mfaSlice.removeMfa({ method }));
      },
    });
  };

  const renderAuthenticatorAppOption = () => {
    return (
      <Row justify='space-between' align='middle'>
        <Space>
          <div className={styles.containerIcon}>
            <Icon name='smartphone' theme={theme} className={styles.icon} />
          </div>

          <div className={styles.description}>
            <LrvText text={t('mfa.app')} theme={theme}/>
            {mfaAuthenticator?.verifiedAt && <LrvText className={styles.verifiedAt} text={`${t('mfa.createdAt')}: ${formatLongDateWithOffset(mfaAuthenticator.verifiedAt)}`} theme={theme}/>}
          </div>
        </Space>

        <Space>
          <ActionButton
            type='primary'
            onClick={() => {
              dispatch(mfaSlice.setShowCreateAuthenticatorAppModal(true));
              dispatch(mfaSlice.generateQrCode());
            }}
          >
            {mfaAuthenticator?.verifiedAt ? t('actions.change') : t('actions.configure') }
          </ActionButton>

          {mfaAuthenticator?.verifiedAt &&
          <ActionButton
            type='text'
            onClick={() => {
              showConfirmRemoveMfa(MultifactorAuthenticationMethod.AUTHENTICATOR_APP);
            }}
          >
            {t('actions.delete')}
          </ActionButton>}
        </Space>
      </Row>
    );
  };

  const renderAuthenticatorEmailOption = () => {
    return (
      <Row justify='space-between' align='middle'>
        <Space>
          <div className={styles.containerIcon}>
            <Icon name='mail' theme={theme} className={styles.icon} />
          </div>

          <div className={styles.description}>
            <LrvText text={t('mfa.email')} theme={theme}/>
            {mfaEmail?.verifiedAt && <LrvText className={styles.verifiedAt} text={`${t('mfa.createdAt')}: ${formatLongDateWithOffset(mfaEmail.verifiedAt)}`} theme={theme}/>}
          </div>
        </Space>

        <Space>
          <ActionButton
            type='primary'
            onClick={() => {
              dispatch(mfaSlice.setShowCreateAuthenticatorEmailModal(true));
            }}
          >
            {mfaEmail?.verifiedAt ? t('actions.change') : t('actions.configure') }
          </ActionButton>

          {mfaEmail?.verifiedAt &&
          <ActionButton
            type='text'
            onClick={() => {
              showConfirmRemoveMfa(MultifactorAuthenticationMethod.EMAIL);
            }}
          >
            {t('actions.delete')}
          </ActionButton>}
        </Space>
      </Row>
    );
  };

  return (
    <div className={cx(styles.container, isLightTheme ? styles.containerLight : styles.containerDark)}>
      <Row justify='space-between' align='middle'>
        <LrvText text={t('mfa.description')} theme={theme}/>
      </Row>
      
      <LrvDivider theme={theme}/>
      {renderAuthenticatorAppOption()}
      <LrvDivider theme={theme}/>
      {renderAuthenticatorEmailOption()}

      <LrvDivider theme={theme}/>

      <AuthenticatorAppForm theme='light'/>
      <AuthenticatorEmailForm theme='light'/>
    </div>
  );
};
